import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from './utils/apiConfig';
import { useAppLogo } from './hooks/useAppLogo';
import './styles-auction-results.css';

function AuctionResults() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [sortConfig, setSortConfig] = useState({ key: 'soldPrice', direction: 'desc' });
  const [filterRole, setFilterRole] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDirectAssignModal, setShowDirectAssignModal] = useState(false);
  const [selectedPlayerForAssign, setSelectedPlayerForAssign] = useState(null);
  const [assignTeamId, setAssignTeamId] = useState('');
  const [assignPrice, setAssignPrice] = useState('');
  const [assignIsGift, setAssignIsGift] = useState(false);
  const [showEditPlayerModal, setShowEditPlayerModal] = useState(false);
  const [selectedPlayerForEdit, setSelectedPlayerForEdit] = useState(null);
  const [editTeamId, setEditTeamId] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editBypassBalance, setEditBypassBalance] = useState(false);
  const [showMoveUnsoldConfirm, setShowMoveUnsoldConfirm] = useState(false);
  const [showMovePendingConfirm, setShowMovePendingConfirm] = useState(false);
  const [showMoveWithdrawnConfirm, setShowMoveWithdrawnConfirm] = useState(false);

  const { logoUrl: appLogoUrl } = useAppLogo();

  const fetchTournament = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/tournaments/${code}`);
      setTournament(res.data.tournament);
    } catch (err) {
      console.error('Error fetching tournament:', err);
    }
  }, [code]);

  const fetchPlayers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/api/players/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const playersData = res.data.players || [];
      setPlayers(playersData);
    } catch (err) {
      console.error('Error fetching players:', err);
      console.error('Error details:', err.response?.data);
    }
  }, [code]);

  const fetchTeams = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/api/auctions/teams/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setTeams(res.data.teams || []);
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  }, [code]);

  // Add body class to hide superadmin banner on this page
  useEffect(() => {
    document.body.classList.add('auction-results-page-active');
    return () => {
      document.body.classList.remove('auction-results-page-active');
    };
  }, []);

  // Handle URL query parameter for tab
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['overview', 'history', 'sold', 'unsold', 'withdrawn', 'pending', 'available', 'teams', 'financial'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Update URL when tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const newSearchParams = new URLSearchParams(searchParams);
    if (tabId === 'overview') {
      newSearchParams.delete('tab');
    } else {
      newSearchParams.set('tab', tabId);
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  // Force Auction handler
  const handleForceAuction = async (playerId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/auctions/${code}/pending/force-auction/${playerId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Player put in force auction (quota bypass enabled)');
      // Refresh data
      await Promise.all([fetchPlayers(), fetchTeams()]);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to put player in force auction');
    }
  };

  // Direct Assign handlers
  const handleOpenDirectAssign = (player) => {
    setSelectedPlayerForAssign(player);
    setAssignPrice(player.basePrice?.toString() || '0');
    setAssignTeamId('');
    setAssignIsGift(false);
    setShowDirectAssignModal(true);
  };

  const handleDirectAssign = async () => {
    if (!selectedPlayerForAssign) return;
    if (!assignTeamId) {
      toast.error('Please select a team');
      return;
    }
    const price = assignIsGift ? 0 : parseFloat(assignPrice);
    if (!assignIsGift && (isNaN(price) || price < 0)) {
      toast.error('Please enter a valid price');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/auctions/${code}/pending/direct-assign/${selectedPlayerForAssign._id}`,
        { teamId: assignTeamId, price },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Player directly assigned to team');
      setShowDirectAssignModal(false);
      setSelectedPlayerForAssign(null);
      setAssignTeamId('');
      setAssignPrice('');
      setAssignIsGift(false);
      // Refresh data
      await Promise.all([fetchPlayers(), fetchTeams()]);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign player');
    }
  };

  // Edit player handlers
  const handleOpenEditPlayer = (player) => {
    setSelectedPlayerForEdit(player);
    setEditTeamId(String(player.soldTo || ''));
    setEditPrice(String(player.soldPrice || '0'));
    setEditBypassBalance(false);
    setShowEditPlayerModal(true);
  };

  const handleUpdatePlayer = async () => {
    if (!selectedPlayerForEdit) return;
    if (!editTeamId) {
      toast.error('Please select a team');
      return;
    }
    const price = parseFloat(editPrice);
    if (isNaN(price) || price < 0) {
      toast.error('Please enter a valid price');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/auctions/${code}/update-sold-player/${selectedPlayerForEdit._id}`,
        { 
          teamId: String(editTeamId), 
          price: price,
          bypassBalanceCheck: editBypassBalance
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Player details updated successfully');
      setShowEditPlayerModal(false);
      setSelectedPlayerForEdit(null);
      setEditTeamId('');
      setEditPrice('');
      setEditBypassBalance(false);
      // Refresh data
      await Promise.all([fetchPlayers(), fetchTeams()]);
    } catch (err) {
      console.error('Update player error:', err);
      console.error('Error response:', err.response?.data);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update player';
      toast.error(errorMessage);
    }
  };

  const handleRevokeSale = async (playerId) => {
    if (!window.confirm('Are you sure you want to revoke this sale? This will refund the team and move the player to withdrawn status.')) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/auctions/${code}/revoke-sale/${playerId}`,
        { reason: 'Sale revoked from auction results page' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Sale revoked successfully');
      // Refresh data
      await Promise.all([fetchPlayers(), fetchTeams()]);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to revoke sale');
    }
  };

  const handleMoveWithdrawnToAvailable = async (playerId) => {
    if (!window.confirm('Are you sure you want to move this player back to the available list?')) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/auctions/${code}/withdrawn/${playerId}/to-available`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Withdrawn player moved to available list');
      // Refresh data
      await Promise.all([fetchPlayers(), fetchTeams()]);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to move player to available');
    }
  };

  const handleMoveAllUnsoldToAvailable = () => {
    if (stats.unsoldPlayers === 0) {
      toast.info('No unsold players to move');
      return;
    }
    setShowMoveUnsoldConfirm(true);
  };

  const confirmMoveAllUnsoldToAvailable = async () => {
    setShowMoveUnsoldConfirm(false);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/auctions/${code}/unsold-to-available`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || 'All unsold players moved to available list');
      // Refresh data
      await Promise.all([fetchPlayers(), fetchTeams()]);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to move unsold players to available');
    }
  };

  const handleMoveAllPendingToAvailable = () => {
    if (stats.pendingPlayers === 0) {
      toast.info('No pending players to move');
      return;
    }
    setShowMovePendingConfirm(true);
  };

  const confirmMoveAllPendingToAvailable = async () => {
    setShowMovePendingConfirm(false);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/auctions/${code}/pending-to-available`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || 'All pending players moved to available list');
      // Refresh data
      await Promise.all([fetchPlayers(), fetchTeams()]);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to move pending players to available');
    }
  };

  const handleMoveAllWithdrawnToAvailable = () => {
    if (stats.withdrawnPlayers === 0) {
      toast.info('No withdrawn players to move');
      return;
    }
    setShowMoveWithdrawnConfirm(true);
  };

  const confirmMoveAllWithdrawnToAvailable = async () => {
    setShowMoveWithdrawnConfirm(false);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/auctions/${code}/withdrawn-to-available`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || 'All withdrawn players moved to available list');
      // Refresh data
      await Promise.all([fetchPlayers(), fetchTeams()]);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to move withdrawn players to available');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTournament(), fetchPlayers(), fetchTeams()]);
      setLoading(false);
    };
    loadData();
  }, [fetchTournament, fetchPlayers, fetchTeams]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalPlayers = players.length;
    // Handle case-insensitive matching for auctionStatus
    const soldPlayers = players.filter(p => {
      const status = String(p.auctionStatus || '').toLowerCase();
      return status === 'sold';
    });
    const unsoldPlayers = players.filter(p => {
      const status = String(p.auctionStatus || '').toLowerCase();
      return status === 'unsold';
    });
    const withdrawnPlayers = players.filter(p => {
      const status = String(p.auctionStatus || '').toLowerCase();
      return status === 'withdrawn';
    });
    const pendingPlayers = players.filter(p => {
      const status = String(p.auctionStatus || '').toLowerCase();
      return status === 'pending';
    });
    const availablePlayers = players.filter(p => {
      const status = String(p.auctionStatus || '').toLowerCase();
      return status === 'available' || !p.auctionStatus || p.auctionStatus === null || p.auctionStatus === '';
    });
    // Calculate total sold value - ensure we only count valid sold prices
    const totalSoldValue = soldPlayers.reduce((sum, p) => {
      const price = Number(p.soldPrice) || 0;
      return sum + price;
    }, 0);
    const avgPrice = soldPlayers.length > 0 ? totalSoldValue / soldPlayers.length : 0;
    const highestBid = soldPlayers.length > 0 ? Math.max(...soldPlayers.map(p => Number(p.soldPrice) || 0)) : 0;
    const highestBidPlayer = soldPlayers.find(p => (p.soldPrice || 0) === highestBid);

    return {
      totalPlayers,
      soldPlayers: soldPlayers.length,
      unsoldPlayers: unsoldPlayers.length,
      withdrawnPlayers: withdrawnPlayers.length,
      pendingPlayers: pendingPlayers.length,
      availablePlayers: availablePlayers.length,
      totalSoldValue,
      avgPrice,
      highestBid,
      highestBidPlayer
    };
  }, [players]);

  // Filter and sort players
  const getFilteredPlayers = (status) => {
    let filtered;
    if (status === 'Available') {
      // For Available, include players with 'Available' status or null/undefined/empty status
      filtered = players.filter(p => {
        const playerStatus = p.auctionStatus;
        return playerStatus === 'Available' || !playerStatus || playerStatus === null || playerStatus === '';
      });
    } else {
      filtered = players.filter(p => p.auctionStatus === status);
    }
    
    if (filterRole) {
      filtered = filtered.filter(p => p.role === filterRole);
    }
    
    if (searchQuery) {
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.playerId?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue, bValue;
      switch (sortConfig.key) {
        case 'soldPrice':
          aValue = a.soldPrice || 0;
          bValue = b.soldPrice || 0;
          break;
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'playerId':
          aValue = a.playerId || '';
          bValue = b.playerId || '';
          break;
        case 'soldAt':
          aValue = a.soldAt ? new Date(a.soldAt).getTime() : 0;
          bValue = b.soldAt ? new Date(b.soldAt).getTime() : 0;
          break;
        default:
          return 0;
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const formatCurrency = (amount) => {
    return `₹${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const normalizeLogoUrl = (logo) => {
    if (!logo) return null;
    if (typeof logo !== 'string') return null;
    if (logo.startsWith('http')) return logo;
    if (logo.startsWith('blob:')) return logo;
    if (logo.startsWith('data:')) return logo;
    if (logo.startsWith('/')) return logo;
    if (logo.startsWith('uploads')) return `/${logo}`;
    return `/${logo}`;
  };

  const tournamentLogoUrl = normalizeLogoUrl(tournament?.logo);

  // const handleExportPDF = async () => {
  //   try {
  //     const token = localStorage.getItem('token');
  //     const response = await axios.get(
  //       `${API_BASE_URL}/api/auctions/${code}/report`,
  //       {
  //         headers: { Authorization: `Bearer ${token}` },
  //         responseType: 'blob'
  //       }
  //     );
  //     const url = window.URL.createObjectURL(new Blob([response.data]));
  //     const link = document.createElement('a');
  //     link.href = url;
  //     link.setAttribute('download', `${code}_Auction_Report.pdf`);
  //     document.body.appendChild(link);
  //     link.click();
  //     link.remove();
  //   } catch (err) {
  //     console.error('Error exporting PDF:', err);
  //     alert('Failed to export PDF. Please try again.');
  //   }
  // };

  // const handleExportExcel = async () => {
  //   try {
  //     const token = localStorage.getItem('token');
  //     const response = await axios.get(
  //       `${API_BASE_URL}/api/reports/players/excel/${code}`,
  //       {
  //         headers: { Authorization: `Bearer ${token}` },
  //         responseType: 'blob'
  //       }
  //     );
  //     const url = window.URL.createObjectURL(new Blob([response.data]));
  //     const link = document.createElement('a');
  //     link.href = url;
  //     link.setAttribute('download', `${code}_Auction_Results.xlsx`);
  //     document.body.appendChild(link);
  //     link.click();
  //     link.remove();
  //   } catch (err) {
  //     console.error('Error exporting Excel:', err);
  //     alert('Failed to export Excel. Please try again.');
  //   }
  // };

  if (loading) {
    return (
      <div className="auction-results-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <h3>Loading Auction Results...</h3>
          <p>Please wait while we fetch the data</p>
        </div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="auction-results-page">
        <div className="error-state">
          <h3>Tournament Not Found</h3>
          <p>The tournament you're looking for doesn't exist.</p>
          <button onClick={() => navigate('/dashboard')} className="btn-primary">Go to Dashboard</button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', count: null },
    { id: 'history', label: 'History', count: stats.soldPlayers },
    { id: 'sold', label: 'Sold', count: stats.soldPlayers },
    { id: 'unsold', label: 'Unsold', count: stats.unsoldPlayers },
    { id: 'withdrawn', label: 'Withdrawn', count: stats.withdrawnPlayers },
    { id: 'pending', label: 'Pending', count: stats.pendingPlayers },
    { id: 'available', label: 'Available', count: stats.availablePlayers },
    { id: 'teams', label: 'Teams', count: teams.length },
    { id: 'financial', label: 'Financial', count: null }
  ];

  return (
    <div className="auction-results-page">
      <div className="auction-results-wrap">
        {/* Sidebar navigation */}
        <aside className="auction-results-nav">
          <div className="auction-results-nav-head">
            <div className="auction-results-brand">
              <div className="auction-results-brand-logos">
                {appLogoUrl ? (
                  <button
                    type="button"
                    className="auction-results-brand-logo auction-results-brand-logo--app"
                    onClick={() => navigate('/')}
                    aria-label="Go to home"
                    title="Home"
                  >
                    <img src={appLogoUrl} alt="App logo" />
                  </button>
                ) : null}

                {tournamentLogoUrl ? (
                  <div
                    className="auction-results-brand-logo auction-results-brand-logo--tournament"
                    aria-label="Tournament logo"
                    title={tournament?.name || 'Tournament'}
                  >
                    <img
                      src={tournamentLogoUrl}
                      alt={`${tournament?.name || 'Tournament'} logo`}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                ) : null}
              </div>

              <div className="auction-results-brand-text">
                <h2 className="auction-results-nav-title">Auction Results</h2>
                {tournament && (
                  <p className="auction-results-nav-meta">{tournament.name} · {code}</p>
                )}
              </div>
            </div>
          </div>
          <nav className="auction-results-nav-list">
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                className={`auction-results-nav-item ${activeTab === tab.id ? 'is-active' : ''}`}
                onClick={() => handleTabChange(tab.id)}
              >
                <span className="auction-results-nav-label">{tab.label}</span>
                {tab.count !== null && <span className="auction-results-nav-count">{tab.count}</span>}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main content */}
        <main className="auction-results-main">
          <div className="auction-results-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="overview-section">
            {/* Key metrics row */}
            <div className="overview-kpi-row">
              <div className="overview-kpi-card">
                <div className="overview-kpi-label">Total Players</div>
                <div className="overview-kpi-value overview-kpi-value--num">{stats.totalPlayers}</div>
              </div>
              <div className="overview-kpi-card overview-kpi-card--success">
                <div className="overview-kpi-label">Sold</div>
                <div className="overview-kpi-value overview-kpi-value--num">{stats.soldPlayers}</div>
              </div>
              <div className="overview-kpi-card overview-kpi-card--primary">
                <div className="overview-kpi-label">Total Value</div>
                <div className="overview-kpi-value overview-kpi-value--currency">{formatCurrency(stats.totalSoldValue)}</div>
              </div>
              <div className="overview-kpi-card">
                <div className="overview-kpi-label">Average Price</div>
                <div className="overview-kpi-value overview-kpi-value--currency">{formatCurrency(stats.avgPrice)}</div>
              </div>
              <div className="overview-kpi-card overview-kpi-card--accent">
                <div className="overview-kpi-label">Highest Bid</div>
                <div className="overview-kpi-value overview-kpi-value--currency">{formatCurrency(stats.highestBid)}</div>
              </div>
            </div>

            {/* Breakdown + progress — all 5 statuses for accuracy */}
            <div className="overview-breakdown-card section-card">
              <h2 className="overview-breakdown-title">Player breakdown</h2>
              <div className="overview-progress-wrap">
                <div className="overview-progress-bar">
                  <div
                    className="overview-progress-fill overview-progress-fill--sold"
                    style={{ width: `${stats.totalPlayers ? (stats.soldPlayers / stats.totalPlayers) * 100 : 0}%` }}
                    title={`Sold: ${stats.soldPlayers}`}
                  />
                  <div
                    className="overview-progress-fill overview-progress-fill--unsold"
                    style={{ width: `${stats.totalPlayers ? (stats.unsoldPlayers / stats.totalPlayers) * 100 : 0}%` }}
                    title={`Unsold: ${stats.unsoldPlayers}`}
                  />
                  <div
                    className="overview-progress-fill overview-progress-fill--withdrawn"
                    style={{ width: `${stats.totalPlayers ? (stats.withdrawnPlayers / stats.totalPlayers) * 100 : 0}%` }}
                    title={`Withdrawn: ${stats.withdrawnPlayers}`}
                  />
                  <div
                    className="overview-progress-fill overview-progress-fill--pending"
                    style={{ width: `${stats.totalPlayers ? (stats.pendingPlayers / stats.totalPlayers) * 100 : 0}%` }}
                    title={`Pending: ${stats.pendingPlayers}`}
                  />
                  <div
                    className="overview-progress-fill overview-progress-fill--available"
                    style={{ width: `${stats.totalPlayers ? (stats.availablePlayers / stats.totalPlayers) * 100 : 0}%` }}
                    title={`Available: ${stats.availablePlayers}`}
                  />
                </div>
                <div className="overview-progress-legend">
                  <span className="overview-legend-item overview-legend-item--sold">Sold {stats.soldPlayers}</span>
                  <span className="overview-legend-item overview-legend-item--unsold">Unsold {stats.unsoldPlayers}</span>
                  <span className="overview-legend-item overview-legend-item--withdrawn">Withdrawn {stats.withdrawnPlayers}</span>
                  <span className="overview-legend-item overview-legend-item--pending">Pending {stats.pendingPlayers}</span>
                  <span className="overview-legend-item overview-legend-item--available">Available {stats.availablePlayers}</span>
                </div>
              </div>
              <div className="overview-breakdown-grid overview-breakdown-grid--5">
                <div className="overview-breakdown-item">
                  <span className="overview-breakdown-label">Sold</span>
                  <span className="overview-breakdown-value overview-breakdown-value--success">{stats.soldPlayers}</span>
                </div>
                <div className="overview-breakdown-item">
                  <span className="overview-breakdown-label">Unsold</span>
                  <span className="overview-breakdown-value overview-breakdown-value--warning">{stats.unsoldPlayers}</span>
                </div>
                <div className="overview-breakdown-item">
                  <span className="overview-breakdown-label">Withdrawn</span>
                  <span className="overview-breakdown-value overview-breakdown-value--error">{stats.withdrawnPlayers}</span>
                </div>
                <div className="overview-breakdown-item">
                  <span className="overview-breakdown-label">Pending</span>
                  <span className="overview-breakdown-value overview-breakdown-value--info">{stats.pendingPlayers}</span>
                </div>
                <div className="overview-breakdown-item">
                  <span className="overview-breakdown-label">Available</span>
                  <span className="overview-breakdown-value overview-breakdown-value--muted">{stats.availablePlayers}</span>
                </div>
              </div>
            </div>

            {/* Highest bid featured */}
            {stats.highestBidPlayer ? (
              <div className="overview-featured section-card">
                <h2 className="overview-featured-title">Highest bid</h2>
                <div className="overview-featured-body">
                  <div className="overview-featured-main">
                    <div className="overview-featured-name">{stats.highestBidPlayer.name}</div>
                    <div className="overview-featured-price">{formatCurrency(stats.highestBidPlayer.soldPrice)}</div>
                  </div>
                  <div className="overview-featured-meta">
                    <div className="overview-featured-meta-item">
                      <span className="overview-featured-meta-label">Team</span>
                      <span className="overview-featured-meta-value">{stats.highestBidPlayer.soldToName || '—'}</span>
                    </div>
                    <div className="overview-featured-meta-item">
                      <span className="overview-featured-meta-label">Role</span>
                      <span className="overview-featured-meta-value">{stats.highestBidPlayer.role || '—'}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="overview-featured section-card overview-featured--empty">
                <h2 className="overview-featured-title">Highest bid</h2>
                <p className="overview-featured-empty-text">No players sold yet.</p>
              </div>
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <HistorySection
            players={players}
            teams={teams}
            formatCurrency={formatCurrency}
            onEdit={handleOpenEditPlayer}
            onRevoke={handleRevokeSale}
          />
        )}

        {/* Sold Players Tab */}
        {activeTab === 'sold' && (
          <PlayersTable
            players={getFilteredPlayers('Sold')}
            title="Sold Players"
            sortConfig={sortConfig}
            onSort={handleSort}
            filterRole={filterRole}
            onFilterRole={setFilterRole}
            searchQuery={searchQuery}
            onSearch={setSearchQuery}
            formatCurrency={formatCurrency}
            showTeam={true}
          />
        )}

        {/* Unsold Players Tab */}
        {activeTab === 'unsold' && (
          <div>
            {stats.unsoldPlayers > 0 && (
              <div style={{ 
                marginBottom: '16px', 
                display: 'flex', 
                justifyContent: 'flex-end',
                gap: '12px',
                alignItems: 'center'
              }}>
                <button
                  onClick={handleMoveAllUnsoldToAvailable}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                  }}
                >
                  Move all unsold to available
                </button>
              </div>
            )}
            <PlayersTable
              players={getFilteredPlayers('Unsold')}
              title="Unsold Players"
              sortConfig={sortConfig}
              onSort={handleSort}
              filterRole={filterRole}
              onFilterRole={setFilterRole}
              searchQuery={searchQuery}
              onSearch={setSearchQuery}
              formatCurrency={formatCurrency}
              showTeam={false}
            />
          </div>
        )}

        {/* Withdrawn Players Tab */}
        {activeTab === 'withdrawn' && (
          <div>
            {stats.withdrawnPlayers > 0 && (
              <div style={{ 
                marginBottom: '16px', 
                display: 'flex', 
                justifyContent: 'flex-end',
                gap: '12px',
                alignItems: 'center'
              }}>
                <button
                  onClick={handleMoveAllWithdrawnToAvailable}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                  }}
                >
                  Move all withdrawn to available
                </button>
              </div>
            )}
            <PlayersTable
              players={getFilteredPlayers('Withdrawn')}
              title="Withdrawn Players"
              sortConfig={sortConfig}
              onSort={handleSort}
              filterRole={filterRole}
              onFilterRole={setFilterRole}
              searchQuery={searchQuery}
              onSearch={setSearchQuery}
              formatCurrency={formatCurrency}
              showTeam={false}
              showReason={true}
              onMoveToAvailable={handleMoveWithdrawnToAvailable}
            />
          </div>
        )}

        {/* Pending Players Tab */}
        {activeTab === 'pending' && (
          <div>
            {stats.pendingPlayers > 0 && (
              <div style={{ 
                marginBottom: '16px', 
                display: 'flex', 
                justifyContent: 'flex-end',
                gap: '12px',
                alignItems: 'center'
              }}>
                <button
                  onClick={handleMoveAllPendingToAvailable}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                  }}
                >
                  Move all pending to available
                </button>
              </div>
            )}
            <PendingPlayersSection
              players={getFilteredPlayers('Pending')}
              teams={teams}
              sortConfig={sortConfig}
              onSort={handleSort}
              filterRole={filterRole}
              onFilterRole={setFilterRole}
              searchQuery={searchQuery}
              onSearch={setSearchQuery}
              formatCurrency={formatCurrency}
              onForceAuction={handleForceAuction}
              onDirectAssign={handleOpenDirectAssign}
            />
          </div>
        )}

        {/* Available Players Tab */}
        {activeTab === 'available' && (
          <PlayersTable
            players={getFilteredPlayers('Available')}
            title="Available Players"
            sortConfig={sortConfig}
            onSort={handleSort}
            filterRole={filterRole}
            onFilterRole={setFilterRole}
            searchQuery={searchQuery}
            onSearch={setSearchQuery}
            formatCurrency={formatCurrency}
            showTeam={false}
          />
        )}

        {/* Teams Tab */}
        {activeTab === 'teams' && (
          <TeamsSection
            teams={teams}
            players={players}
            formatCurrency={formatCurrency}
          />
        )}

        {/* Financial Tab */}
        {activeTab === 'financial' && (
          <FinancialSection
            stats={stats}
            teams={teams}
            players={players}
            formatCurrency={formatCurrency}
          />
        )}
          </div>
        </main>
      </div>

      {/* Direct Assignment Modal */}
      {showDirectAssignModal && selectedPlayerForAssign && (
        <div className="modal-overlay" onClick={() => setShowDirectAssignModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Direct Assign Player</h3>
              <button onClick={() => setShowDirectAssignModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '20px' }}>
                <div style={{ 
                  padding: '12px', 
                  background: 'rgba(59, 130, 246, 0.08)', 
                  borderRadius: '8px',
                  marginBottom: '16px',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  color: '#1e293b'
                }}>
                  <div><strong>Player:</strong> {selectedPlayerForAssign.name}</div>
                  <div><strong>Role:</strong> {selectedPlayerForAssign.role || 'N/A'}</div>
                  <div><strong>Base Price:</strong> ₹{selectedPlayerForAssign.basePrice?.toLocaleString() || '0'}</div>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#475569' }}>
                  Select Team *
                </label>
                <select
                  value={assignTeamId}
                  onChange={(e) => {
                    setAssignTeamId(e.target.value);
                    const selectedTeam = teams.find(t => String(t._id) === String(e.target.value));
                    if (selectedTeam) {
                      const balance = selectedTeam.currentBalance || selectedTeam.budgetBalance || 0;
                      if (parseFloat(assignPrice) > balance) {
                        setAssignPrice(Math.min(parseFloat(assignPrice) || 0, balance).toString());
                      }
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    background: '#ffffff',
                    color: '#1e293b',
                    fontSize: '14px'
                  }}
                >
                  <option value="">-- Select Team --</option>
                  {teams.map((team) => {
                    const isFull = team.isQuotaFull || team.remainingPlayers === 0;
                    const balance = team.currentBalance || team.budgetBalance || 0;
                    const balanceFormatted = balance.toLocaleString('en-IN', { maximumFractionDigits: 0 });
                    return (
                      <option key={team._id} value={team._id}>
                        {team.name} {isFull ? `(FULL - ${team.playersBought || 0}/${team.maxPlayers || 16})` : `(${team.playersBought || 0}/${team.maxPlayers || 16})`} | Balance: ₹{balanceFormatted}
                      </option>
                    );
                  })}
                </select>
                {assignTeamId && (() => {
                  const selectedTeam = teams.find(t => String(t._id) === String(assignTeamId));
                  if (!selectedTeam) return null;
                  
                  const isFull = selectedTeam.isQuotaFull || selectedTeam.remainingPlayers === 0;
                  const balance = selectedTeam.currentBalance || selectedTeam.budgetBalance || 0;
                  const balanceFormatted = balance.toLocaleString('en-IN', { maximumFractionDigits: 0 });
                  
                  return (
                    <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{
                        padding: '10px',
                        background: 'rgba(59, 130, 246, 0.08)',
                        border: '1px solid rgba(59, 130, 246, 0.2)',
                        borderRadius: '6px',
                        color: '#1e293b',
                        fontSize: '13px'
                      }}>
                        💰 <strong>Team Balance:</strong> ₹{balanceFormatted}
                      </div>
                      {isFull && (
                        <div style={{
                          padding: '10px',
                          background: 'rgba(245, 158, 11, 0.1)',
                          border: '1px solid rgba(245, 158, 11, 0.3)',
                          borderRadius: '6px',
                          color: 'var(--admin-accent-warning, #f59e0b)',
                          fontSize: '13px'
                        }}>
                          ⚠️ This team is at full quota. Assignment will bypass quota limit.
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px', gap: '8px' }}>
                  <input
                    type="checkbox"
                    id="assignIsGift"
                    checked={assignIsGift}
                    onChange={(e) => {
                      setAssignIsGift(e.target.checked);
                      if (e.target.checked) {
                        setAssignPrice('0');
                      } else {
                        setAssignPrice(selectedPlayerForAssign.basePrice?.toString() || '0');
                      }
                    }}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <label 
                    htmlFor="assignIsGift" 
                    style={{ 
                      cursor: 'pointer', 
                      fontWeight: '500', 
                      color: '#475569',
                      fontSize: '14px',
                      userSelect: 'none'
                    }}
                  >
                    Assign as gift / free (₹0)
                  </label>
                </div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#475569' }}>
                  Price (₹) {!assignIsGift && '*'}
                </label>
                <input
                  type="number"
                  value={assignPrice}
                  onChange={(e) => setAssignPrice(e.target.value)}
                  min="0"
                  step="100"
                  disabled={assignIsGift}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    background: assignIsGift ? '#f1f5f9' : '#ffffff',
                    color: assignIsGift ? '#94a3b8' : '#1e293b',
                    fontSize: '14px',
                    cursor: assignIsGift ? 'not-allowed' : 'text'
                  }}
                  placeholder={assignIsGift ? "Free (Gift)" : "Enter price"}
                />
                {assignIsGift && (
                  <div style={{
                    marginTop: '8px',
                    padding: '10px',
                    background: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '6px',
                    color: '#16a34a',
                    fontSize: '13px'
                  }}>
                    ✓ Player will be assigned for free (₹0)
                  </div>
                )}
                {assignTeamId && assignPrice && !assignIsGift && (() => {
                  const selectedTeam = teams.find(t => String(t._id) === String(assignTeamId));
                  const price = parseFloat(assignPrice);
                  if (!isNaN(price) && selectedTeam) {
                    const balance = selectedTeam.currentBalance || selectedTeam.budgetBalance || 0;
                    if (price > balance) {
                      return (
                        <div style={{
                          marginTop: '8px',
                          padding: '10px',
                          background: 'rgba(239, 68, 68, 0.1)',
                          border: '1px solid rgba(239, 68, 68, 0.3)',
                          borderRadius: '6px',
                          color: 'var(--admin-accent-error, #ef4444)',
                          fontSize: '13px'
                        }}>
                          ⚠️ Team balance insufficient. Available: ₹{balance.toLocaleString()}
                        </div>
                      );
                    }
                  }
                  return null;
                })()}
              </div>

              <div style={{ 
                padding: '12px',
                background: 'rgba(34, 197, 94, 0.08)',
                border: '1px solid rgba(34, 197, 94, 0.2)',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '13px',
                color: '#475569'
              }}>
                <strong style={{ color: '#1e293b' }}>Note:</strong> This will directly assign the player to the selected team, bypassing quota limits if necessary. {assignIsGift && 'The player will be assigned as a gift (₹0).'}
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button 
                  className="btn-action btn-history"
                  onClick={() => {
                    setShowDirectAssignModal(false);
                    setSelectedPlayerForAssign(null);
                    setAssignTeamId('');
                    setAssignPrice('');
                    setAssignIsGift(false);
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="btn-action btn-sold"
                  onClick={handleDirectAssign}
                  disabled={!assignTeamId || (!assignIsGift && !assignPrice)}
                  style={{
                    opacity: (!assignTeamId || (!assignIsGift && !assignPrice)) ? 0.5 : 1,
                    cursor: (!assignTeamId || (!assignIsGift && !assignPrice)) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {assignIsGift ? 'Assign as gift' : 'Assign player'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Player Modal */}
      {showEditPlayerModal && selectedPlayerForEdit && (
        <div className="modal-overlay" onClick={() => setShowEditPlayerModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Edit Sold Player</h3>
              <button onClick={() => setShowEditPlayerModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '20px' }}>
                <div style={{ 
                  padding: '12px', 
                  background: 'rgba(59, 130, 246, 0.08)', 
                  borderRadius: '8px',
                  marginBottom: '16px',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  color: '#1e293b'
                }}>
                  <div><strong>Player:</strong> {selectedPlayerForEdit.name}</div>
                  <div><strong>Role:</strong> {selectedPlayerForEdit.role || 'N/A'}</div>
                  <div><strong>Current Team:</strong> {selectedPlayerForEdit.soldToName || 'N/A'}</div>
                  <div><strong>Current Price:</strong> ₹{selectedPlayerForEdit.soldPrice?.toLocaleString() || '0'}</div>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#475569' }}>
                  Change Team *
                </label>
                <select
                  value={editTeamId}
                  onChange={(e) => {
                    setEditTeamId(e.target.value);
                    const selectedTeam = teams.find(t => String(t._id) === String(e.target.value));
                    if (selectedTeam) {
                      const balance = selectedTeam.currentBalance || selectedTeam.budgetBalance || 0;
                      if (parseFloat(editPrice) > balance) {
                        setEditPrice(Math.min(parseFloat(editPrice) || 0, balance).toString());
                      }
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    background: '#ffffff',
                    color: '#1e293b',
                    fontSize: '14px'
                  }}
                >
                  <option value="">-- Select Team --</option>
                  {teams.map((team) => {
                    const isFull = team.isQuotaFull || team.remainingPlayers === 0;
                    const isCurrentTeam = String(team._id) === String(selectedPlayerForEdit.soldTo);
                    return (
                      <option key={team._id} value={team._id}>
                        {team.name} {isCurrentTeam ? '(Current)' : ''} {isFull ? `(FULL - ${team.playersBought || 0}/${team.maxPlayers || 16})` : `(${team.playersBought || 0}/${team.maxPlayers || 16})`}
                      </option>
                    );
                  })}
                </select>
                {editTeamId && (() => {
                  const selectedTeam = teams.find(t => String(t._id) === String(editTeamId));
                  const isFull = selectedTeam?.isQuotaFull || selectedTeam?.remainingPlayers === 0;
                  const isCurrentTeam = String(editTeamId) === String(selectedPlayerForEdit.soldTo);
                  if (isFull && !isCurrentTeam) {
                    return (
                      <div style={{
                        marginTop: '8px',
                        padding: '10px',
                        background: 'rgba(245, 158, 11, 0.1)',
                        border: '1px solid rgba(245, 158, 11, 0.3)',
                        borderRadius: '6px',
                        color: '#f59e0b',
                        fontSize: '13px'
                      }}>
                        ⚠️ This team is at full quota. Change will bypass quota limit.
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#475569' }}>
                  Change Price (₹) *
                </label>
                <input
                  type="number"
                  value={editPrice}
                  onChange={(e) => setEditPrice(e.target.value)}
                  min="0"
                  step="100"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    background: '#ffffff',
                    color: '#1e293b',
                    fontSize: '14px'
                  }}
                  placeholder="Enter price"
                />
                {editTeamId && editPrice && (() => {
                  const selectedTeam = teams.find(t => String(t._id) === String(editTeamId));
                  const price = parseFloat(editPrice);
                  if (!isNaN(price) && selectedTeam) {
                    const balance = selectedTeam.currentBalance || selectedTeam.budgetBalance || 0;
                    const currentPrice = selectedPlayerForEdit.soldPrice || 0;
                    const priceDiff = price - currentPrice;
                    const newBalance = balance - priceDiff;
                    
                    if (newBalance < 0) {
                      return (
                        <div style={{
                          marginTop: '8px',
                          padding: '10px',
                          background: 'rgba(239, 68, 68, 0.1)',
                          border: '1px solid rgba(239, 68, 68, 0.3)',
                          borderRadius: '6px',
                          color: '#ef4444',
                          fontSize: '13px'
                        }}>
                          ⚠️ Team balance insufficient. Available: ₹{balance.toLocaleString()}, Price difference: ₹{priceDiff.toLocaleString()}
                        </div>
                      );
                    }
                  }
                  return null;
                })()}
              </div>

              {editTeamId && editPrice && (() => {
                const selectedTeam = teams.find(t => String(t._id) === String(editTeamId));
                const price = parseFloat(editPrice);
                if (!isNaN(price) && selectedTeam) {
                  const balance = selectedTeam.currentBalance || selectedTeam.budgetBalance || 0;
                  const currentPrice = selectedPlayerForEdit.soldPrice || 0;
                  const isCurrentTeam = String(editTeamId) === String(selectedPlayerForEdit.soldTo);
                  const priceDiff = isCurrentTeam ? (price - currentPrice) : price;
                  const newBalance = isCurrentTeam ? (balance - priceDiff) : (balance - price);
                  
                  if (newBalance < 0) {
                    return (
                      <div style={{
                        marginBottom: '16px',
                        padding: '12px',
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: '8px',
                        fontSize: '13px'
                      }}>
                        <div style={{ color: '#ef4444', marginBottom: '10px' }}>
                          ⚠️ Team balance insufficient. Available: ₹{balance.toLocaleString()}, Required: ₹{isCurrentTeam ? priceDiff.toLocaleString() : price.toLocaleString()}
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#ef4444' }}>
                          <input
                            type="checkbox"
                            checked={editBypassBalance}
                            onChange={(e) => setEditBypassBalance(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                          />
                          <span>Bypass balance check (Admin override)</span>
                        </label>
                      </div>
                    );
                  }
                }
                return null;
              })()}

              <div style={{ 
                padding: '12px',
                background: 'rgba(59, 130, 246, 0.08)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '13px',
                color: '#475569'
              }}>
                <strong style={{ color: '#1e293b' }}>Note:</strong> This will update the player's team and/or price. The team balances will be recalculated automatically.
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button 
                  className="btn-action btn-history"
                  onClick={() => {
                    setShowEditPlayerModal(false);
                    setSelectedPlayerForEdit(null);
                    setEditTeamId('');
                    setEditPrice('');
                    setEditBypassBalance(false);
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="btn-action btn-sold"
                  onClick={handleUpdatePlayer}
                  disabled={!editTeamId || !editPrice}
                  style={{
                    opacity: (!editTeamId || !editPrice) ? 0.5 : 1,
                    cursor: (!editTeamId || !editPrice) ? 'not-allowed' : 'pointer'
                  }}
                >
                  Update Player
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Move All Unsold to Available Confirmation Modal */}
      {showMoveUnsoldConfirm && (
        <div className="modal-overlay" onClick={() => setShowMoveUnsoldConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Move All Unsold Players to Available</h2>
              <button className="modal-close" onClick={() => setShowMoveUnsoldConfirm(false)}>×</button>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ marginBottom: '20px', fontSize: '16px', lineHeight: '1.5' }}>
                Are you sure you want to move all unsold players to the available list? This will reset their bid history and make them available for bidding again.
              </p>
              {stats.unsoldPlayers > 0 && (
                <div style={{ marginBottom: '20px', padding: '15px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '14px', color: '#374151' }}>
                    All {stats.unsoldPlayers} unsold player(s) will be moved to available list and their bid history will be reset
                  </div>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', padding: '20px', borderTop: '1px solid #e5e7eb' }}>
              <button
                className="btn-secondary-new"
                onClick={() => setShowMoveUnsoldConfirm(false)}
                style={{ padding: '10px 20px' }}
              >
                Cancel
              </button>
              <button
                className="btn-primary-new"
                onClick={confirmMoveAllUnsoldToAvailable}
                style={{ padding: '10px 20px' }}
              >
                Confirm & move to available
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move All Pending to Available Confirmation Modal */}
      {showMovePendingConfirm && (
        <div className="modal-overlay" onClick={() => setShowMovePendingConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Move All Pending Players to Available</h2>
              <button className="modal-close" onClick={() => setShowMovePendingConfirm(false)}>×</button>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ marginBottom: '20px', fontSize: '16px', lineHeight: '1.5' }}>
                Are you sure you want to move all pending players to the available list? This will make them available for bidding again.
              </p>
              {stats.pendingPlayers > 0 && (
                <div style={{ marginBottom: '20px', padding: '15px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '14px', color: '#374151' }}>
                    All {stats.pendingPlayers} pending player(s) will be moved to available list
                  </div>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', padding: '20px', borderTop: '1px solid #e5e7eb' }}>
              <button
                className="btn-secondary-new"
                onClick={() => setShowMovePendingConfirm(false)}
                style={{ padding: '10px 20px' }}
              >
                Cancel
              </button>
              <button
                className="btn-primary-new"
                onClick={confirmMoveAllPendingToAvailable}
                style={{ padding: '10px 20px' }}
              >
                Confirm & move to available
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move All Withdrawn to Available Confirmation Modal */}
      {showMoveWithdrawnConfirm && (
        <div className="modal-overlay" onClick={() => setShowMoveWithdrawnConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Move All Withdrawn Players to Available</h2>
              <button className="modal-close" onClick={() => setShowMoveWithdrawnConfirm(false)}>×</button>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ marginBottom: '20px', fontSize: '16px', lineHeight: '1.5' }}>
                Are you sure you want to move all withdrawn players to the available list? This will reset their bid history and withdrawal reasons, and make them available for bidding again.
              </p>
              {stats.withdrawnPlayers > 0 && (
                <div style={{ marginBottom: '20px', padding: '15px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '14px', color: '#374151' }}>
                    All {stats.withdrawnPlayers} withdrawn player(s) will be moved to available list and their bid history and withdrawal reasons will be reset
                  </div>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', padding: '20px', borderTop: '1px solid #e5e7eb' }}>
              <button
                className="btn-secondary-new"
                onClick={() => setShowMoveWithdrawnConfirm(false)}
                style={{ padding: '10px 20px' }}
              >
                Cancel
              </button>
              <button
                className="btn-primary-new"
                onClick={confirmMoveAllWithdrawnToAvailable}
                style={{ padding: '10px 20px' }}
              >
                Confirm & move to available
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Players Table Component
function PlayersTable({ players, title, sortConfig, onSort, filterRole, onFilterRole, searchQuery, onSearch, formatCurrency, showTeam, showReason, onMoveToAvailable }) {
  const roles = [...new Set(players.map(p => p.role).filter(Boolean))];
  const [isPhone, setIsPhone] = useState(() => window.innerWidth <= 600);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 600px)');
    const onChange = (e) => setIsPhone(e.matches);
    setIsPhone(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  return (
    <div className="section-card">
      <div className="section-header">
        <h2 className="section-title">{title}</h2>
        <div className="section-controls">
          <input
            type="text"
            placeholder="Search players..."
            value={searchQuery}
            onChange={(e) => onSearch(e.target.value)}
            className="search-input"
          />
          {roles.length > 0 && (
            <select
              value={filterRole}
              onChange={(e) => onFilterRole(e.target.value)}
              className="filter-select"
            >
              <option value="">All Roles</option>
              {roles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {players.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h3>No players found</h3>
          <p>There are no players in this category.</p>
        </div>
      ) : (
        <>
          {isPhone ? (
            <div className="players-cards" role="list">
              {players.map((player) => {
                const displayId = player.playerId
                  ? (player.playerId.includes('-') ? player.playerId.split('-').slice(1).join('-') : player.playerId)
                  : 'N/A';
                const whenText = showTeam ? (player.soldAt ? new Date(player.soldAt).toLocaleString() : 'N/A') : null;
                const amountText = showTeam ? formatCurrency(player.soldPrice) : formatCurrency(player.basePrice);
                return (
                  <div key={player._id} className="players-card-item" role="listitem">
                    <div className="players-card-top">
                      <div className="players-card-left">
                        <div className="players-card-name">{player.name || 'N/A'}</div>
                        <div className="players-card-sub">
                          <span className="players-card-playerid">{displayId}</span>
                          {whenText ? (
                            <>
                              <span className="players-card-dot" aria-hidden="true">•</span>
                              <span className="players-card-time">{whenText}</span>
                            </>
                          ) : null}
                        </div>
                      </div>
                      <div className="players-card-right">
                        <div className={`players-card-amount ${showTeam ? 'is-sold' : 'is-base'}`}>{amountText}</div>
                      </div>
                    </div>

                    {showTeam ? (
                      <div className="players-card-mid">
                        <span className="team-badge players-card-team">{player.soldToName || 'N/A'}</span>
                        {player.transactionType ? (
                          <span
                            className={`players-card-chip ${
                              player.transactionType === 'DirectAssign'
                                ? 'is-direct'
                                : player.transactionType === 'ForceAuction'
                                  ? 'is-force'
                                  : ''
                            }`}
                          >
                            {player.transactionType === 'DirectAssign'
                              ? 'Direct assign'
                              : player.transactionType === 'ForceAuction'
                                ? 'Force auction'
                                : player.transactionType}
                          </span>
                        ) : null}
                      </div>
                    ) : null}

                    {showReason ? (
                      <div className="players-card-reason">
                        <span className="players-card-reason-label">Reason</span>
                        <span className="players-card-reason-value">{player.withdrawalReason || 'N/A'}</span>
                      </div>
                    ) : null}

                    {onMoveToAvailable ? (
                      <div className="players-card-actions">
                        <button type="button" className="players-card-action players-card-action--move" onClick={() => onMoveToAvailable(player._id)}>
                          Move to available
                        </button>
                      </div>
                    ) : null}
                  </div>
                );
              })}

              <div className="players-cards-footer">
                Showing {players.length} player{players.length !== 1 ? 's' : ''}
              </div>
            </div>
          ) : (
            <div className="table-container">
              <table className="results-table">
                <thead>
                  <tr>
                    <th onClick={() => onSort('name')} className="sortable">
                      Player Name {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => onSort('playerId')} className="sortable">
                      Player ID {sortConfig.key === 'playerId' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    {showTeam && (
                      <th>Team</th>
                    )}
                    {showTeam && (
                      <th onClick={() => onSort('soldPrice')} className="sortable text-right">
                        Price {sortConfig.key === 'soldPrice' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </th>
                    )}
                    {!showTeam && (
                      <th className="text-right">Base Price</th>
                    )}
                    {showTeam && (
                      <th onClick={() => onSort('soldAt')} className="sortable text-right">
                        Sold At {sortConfig.key === 'soldAt' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </th>
                    )}
                    {showReason && (
                      <th>Reason</th>
                    )}
                    {onMoveToAvailable && (
                      <th>Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {players.map(player => (
                    <tr key={player._id}>
                      <td>
                        <div className="player-name-cell">
                          <span className="player-name">{player.name}</span>
                        </div>
                      </td>
                      <td>
                        {(player.playerId && (() => {
                          // Remove tournament code prefix (e.g., "PLTC002-001" -> "001")
                          const displayId = player.playerId.includes('-') 
                            ? player.playerId.split('-').slice(1).join('-')
                            : player.playerId;
                          return <span className="player-id">{displayId}</span>;
                        })()) || 'N/A'}
                      </td>
                      {showTeam && (
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span className="team-badge">{player.soldToName || 'N/A'}</span>
                            {player.transactionType === 'DirectAssign' && (
                              <span style={{
                                fontSize: '11px',
                                color: '#8b5cf6',
                                background: 'rgba(139, 92, 246, 0.1)',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                display: 'inline-block',
                                width: 'fit-content'
                              }}>Direct assign</span>
                            )}
                            {player.transactionType === 'ForceAuction' && (
                              <span style={{
                                fontSize: '11px',
                                color: '#f59e0b',
                                background: 'rgba(245, 158, 11, 0.1)',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                display: 'inline-block',
                                width: 'fit-content'
                              }}>Force auction</span>
                            )}
                          </div>
                        </td>
                      )}
                      {showTeam && (
                        <td className="price-cell">{formatCurrency(player.soldPrice)}</td>
                      )}
                      {!showTeam && (
                        <td className="price-cell">{formatCurrency(player.basePrice)}</td>
                      )}
                      {showTeam && (
                        <td className="text-right">
                          {player.soldAt ? new Date(player.soldAt).toLocaleString() : 'N/A'}
                        </td>
                      )}
                      {showReason && (
                        <td>{player.withdrawalReason || 'N/A'}</td>
                      )}
                      {onMoveToAvailable && (
                        <td>
                          <button
                            onClick={() => onMoveToAvailable(player._id)}
                            style={{
                              padding: '6px 12px',
                              fontSize: '12px',
                              background: '#22c55e',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                            title="Move player back to available list"
                          >
                            Move to available
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="table-footer">
                <div className="table-count">Showing {players.length} player{players.length !== 1 ? 's' : ''}</div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Teams Section Component
function TeamsSection({ teams, players, formatCurrency }) {
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [enlargedImage, setEnlargedImage] = useState(null);
  
  const teamStats = teams.map(team => {
    // Use accurate data from API (computeTeamSnapshots) if available
    const playersBought = team.playersBought || 0;
    const totalSpent = team.totalSpent || 0;
    const currentBalance = team.currentBalance || 0;
    const budget = team.budget || 0;
    
    // Calculate derived stats
    const avgPrice = playersBought > 0 ? totalSpent / playersBought : 0;
    
    // Get highest bid from players data for accuracy
    const teamPlayers = players.filter(p => p.auctionStatus === 'Sold' && String(p.soldTo) === String(team._id));
    const highestBid = teamPlayers.length > 0 ? Math.max(...teamPlayers.map(p => p.soldPrice || 0), 0) : 0;

    return {
      ...team,
      playersCount: playersBought,
      totalSpent,
      avgPrice,
      highestBid,
      balance: currentBalance,
      budget
    };
  }).sort((a, b) => b.totalSpent - a.totalSpent);

  const handleTeamClick = (team) => {
    setSelectedTeam(team);
  };

  const handleCloseModal = () => {
    setSelectedTeam(null);
  };

  // Get players for selected team
  const teamPlayers = selectedTeam
    ? players.filter(p => {
        const status = String(p.auctionStatus || '').toLowerCase();
        return status === 'sold' && String(p.soldTo) === String(selectedTeam._id);
      })
    : [];

  const buildPhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith('http')) return photo;
    if (photo.startsWith('blob:')) return photo;
    if (photo.startsWith('data:')) return photo;
    
    // For relative paths, prepend API_BASE_URL
    let path = photo;
    if (path.startsWith('/')) {
      path = path.slice(1); // Remove leading slash
    }
    if (path.startsWith('uploads')) {
      return `${API_BASE_URL}/${path}`;
    }
    return `${API_BASE_URL}/uploads/${path}`;
  };

  const buildLogoUrl = (logo) => {
    if (!logo) return '/default-logo.png';
    if (logo.startsWith('http')) return logo;
    if (logo.startsWith('blob:')) return logo;
    if (logo.startsWith('data:')) return logo;
    
    // For relative paths, prepend API_BASE_URL
    let path = logo;
    if (path.startsWith('/')) {
      path = path.slice(1); // Remove leading slash
    }
    if (path.startsWith('uploads')) {
      return `${API_BASE_URL}/${path}`;
    }
    return `${API_BASE_URL}/${path}`;
  };

  return (
    <>
      <div className="section-card">
        <h2 className="section-title">Team Performance</h2>
        <div className="table-container">
          <table className="results-table">
            <thead>
              <tr>
                <th>Team Name</th>
                <th className="text-right">Players Bought</th>
                <th className="text-right">Total Spent</th>
                <th className="text-right">Average Price</th>
                <th className="text-right">Highest Bid</th>
                <th className="text-right">Budget</th>
                <th className="text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {teamStats.map(team => (
                <tr 
                  key={team._id} 
                  onClick={() => handleTeamClick(team)}
                  style={{ cursor: 'pointer' }}
                  className="team-row-clickable"
                >
                  <td>
                    <div className="team-name-cell">
                      {team.logo && (
                        <img src={buildLogoUrl(team.logo)} 
                             alt={team.name} className="team-logo-small" />
                      )}
                      <span>{team.name}</span>
                    </div>
                  </td>
                  <td className="num-cell">{team.playersCount}</td>
                  <td className="price-cell">{formatCurrency(team.totalSpent)}</td>
                  <td className="price-cell">{formatCurrency(team.avgPrice)}</td>
                  <td className="price-cell">{formatCurrency(team.highestBid)}</td>
                  <td className="price-cell">{formatCurrency(team.budget)}</td>
                  <td className={team.balance >= 0 ? 'balance-positive' : 'balance-negative'}>
                    {formatCurrency(team.balance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Team Players Modal */}
      {selectedTeam && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content team-players-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                {selectedTeam.logo && (
                  <img 
                    src={buildLogoUrl(selectedTeam.logo)} 
                    alt={selectedTeam.name} 
                    style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                  />
                )}
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0 }}>{selectedTeam.name} - Player Roster</h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                    {teamPlayers.length} {teamPlayers.length === 1 ? 'player' : 'players'} • Total Spent: {formatCurrency(selectedTeam.totalSpent)}
                  </p>
                </div>
              </div>
              <button onClick={handleCloseModal}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              {teamPlayers.length === 0 ? (
                <div className="empty-state" style={{ padding: '40px 20px' }}>
                  <div className="empty-icon">👥</div>
                  <h3>No players yet</h3>
                  <p>This team hasn't purchased any players yet.</p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="results-table team-players-table">
                    <thead>
                      <tr>
                        <th>Photo</th>
                        <th>Player Name</th>
                        <th>Player ID</th>
                        <th>Role</th>
                        <th className="text-right">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamPlayers.map((player) => {
                        const photoUrl = buildPhotoUrl(player.photo);
                        const playerInitial = player.name?.charAt(0) || 'P';
                        const displayId = (player.playerId && (() => {
                          return player.playerId.includes('-') 
                            ? player.playerId.split('-').slice(1).join('-')
                            : player.playerId;
                        })()) || 'N/A';
                        return (
                          <tr key={player._id}>
                            <td>
                              <div 
                                className="team-player-table-photo-wrapper"
                                onClick={() => {
                                  if (photoUrl) {
                                    setEnlargedImage({ url: photoUrl, name: player.name });
                                  }
                                }}
                                style={{ cursor: photoUrl ? 'pointer' : 'default' }}
                                title={photoUrl ? 'Click to enlarge' : ''}
                              >
                                {photoUrl ? (
                                  <img
                                    src={photoUrl}
                                    alt={player.name}
                                    className="team-player-table-photo"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      const placeholder = e.target.nextElementSibling;
                                      if (placeholder) placeholder.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div 
                                  className="team-player-table-photo-placeholder"
                                  style={{ display: photoUrl ? 'none' : 'flex' }}
                                >
                                  <span>{playerInitial}</span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="player-name-cell">
                                <span className="player-name">{player.name || 'N/A'}</span>
                              </div>
                            </td>
                            <td>
                              <span className="player-id">{displayId}</span>
                            </td>
                            <td>
                              <span className="team-player-role-badge">{player.role || 'N/A'}</span>
                            </td>
                            <td className="price-cell">{formatCurrency(player.soldPrice)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Enlarged Image Modal */}
      {enlargedImage && (
        <div 
          className="modal-overlay image-enlarge-overlay" 
          onClick={() => setEnlargedImage(null)}
          style={{ zIndex: 10000 }}
        >
          <div 
            className="enlarged-image-container"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="close-enlarged-image"
              onClick={() => setEnlargedImage(null)}
            >
              ×
            </button>
            <img 
              src={enlargedImage.url} 
              alt={enlargedImage.name}
              className="enlarged-player-image"
            />
            <div className="enlarged-image-name">{enlargedImage.name}</div>
          </div>
        </div>
      )}
    </>
  );
}

// Financial Section Component
function FinancialSection({ stats, teams, players, formatCurrency }) {
  // Use accurate data from API (computeTeamSnapshots)
  const totalBudget = teams.reduce((sum, team) => sum + (team.budget || 0), 0);
  // Use totalSpent from teams API for accuracy (aggregated from database)
  const totalSpentFromTeams = teams.reduce((sum, team) => sum + (team.totalSpent || 0), 0);
  // Fallback to stats if teams data not available
  const totalSpent = totalSpentFromTeams > 0 ? totalSpentFromTeams : stats.totalSoldValue;
  const totalRemaining = teams.reduce((sum, team) => sum + (team.currentBalance || 0), 0);
  const utilizationRate = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;

  const roleDistribution = {};
  players.filter(p => String(p.auctionStatus || '').toLowerCase() === 'sold').forEach(player => {
    const role = player.role || 'Unknown';
    if (!roleDistribution[role]) {
      roleDistribution[role] = { count: 0, total: 0 };
    }
    roleDistribution[role].count++;
    roleDistribution[role].total += Number(player.soldPrice) || 0;
  });

  return (
    <div className="financial-section">
      <div className="section-card">
        <h2 className="section-title">Financial Summary</h2>
        <div className="financial-grid">
          <div className="financial-card">
            <div className="financial-label">Total Budget</div>
            <div className="financial-value">{formatCurrency(totalBudget)}</div>
          </div>
          <div className="financial-card">
            <div className="financial-label">Total Spent</div>
            <div className="financial-value spent">{formatCurrency(totalSpent)}</div>
          </div>
          <div className="financial-card">
            <div className="financial-label">Total Remaining</div>
            <div className="financial-value remaining">{formatCurrency(totalRemaining)}</div>
          </div>
          <div className="financial-card">
            <div className="financial-label">Budget Utilization</div>
            <div className="financial-value">{utilizationRate.toFixed(1)}%</div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${Math.min(utilizationRate, 100)}%` }}></div>
            </div>
          </div>
        </div>
      </div>

      <div className="section-card">
        <h2 className="section-title">Role-wise Distribution</h2>
        <div className="table-container">
          <table className="results-table">
            <thead>
              <tr>
                <th>Role</th>
                <th className="text-right">Players Sold</th>
                <th className="text-right">Total Value</th>
                <th className="text-right">Average Price</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(roleDistribution)
                .sort((a, b) => b[1].total - a[1].total)
                .map(([role, data]) => (
                  <tr key={role}>
                    <td>{role}</td>
                    <td className="num-cell">{data.count}</td>
                    <td className="price-cell">{formatCurrency(data.total)}</td>
                    <td className="price-cell">{formatCurrency(data.count > 0 ? data.total / data.count : 0)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Pending Players Section Component
function PendingPlayersSection({ players, teams, sortConfig, onSort, filterRole, onFilterRole, searchQuery, onSearch, formatCurrency, onForceAuction, onDirectAssign }) {
  const roles = [...new Set(players.map(p => p.role).filter(Boolean))];
  const [isPhone, setIsPhone] = useState(() => window.innerWidth <= 600);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 600px)');
    const onChange = (e) => setIsPhone(e.matches);
    setIsPhone(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  return (
    <div className="section-card">
      <div className="section-header">
        <h2 className="section-title">Pending Players</h2>
        <div className="section-controls">
          <input
            type="text"
            placeholder="Search players..."
            value={searchQuery}
            onChange={(e) => onSearch(e.target.value)}
            className="search-input"
          />
          {roles.length > 0 && (
            <select
              value={filterRole}
              onChange={(e) => onFilterRole(e.target.value)}
              className="filter-select"
            >
              <option value="">All Roles</option>
              {roles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {players.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h3>No pending players</h3>
          <p>There are no players in the pending list.</p>
        </div>
      ) : (
        <>
          <div style={{ 
            marginBottom: '16px', 
            padding: '12px', 
            background: 'rgba(245, 158, 11, 0.08)', 
            borderRadius: '8px',
            border: '1px solid rgba(245, 158, 11, 0.2)',
            fontSize: '14px',
            color: '#92400e'
          }}>
            ⚠️ <strong style={{ color: '#78350f' }}>Note:</strong> These players are pending assignment. You can use <strong style={{ color: '#78350f' }}>Force Auction</strong> to put them back in auction (bypassing quota) or <strong style={{ color: '#78350f' }}>Direct Assign</strong> to manually assign them to a team.
          </div>
          {isPhone ? (
            <div className="players-cards pending-cards" role="list">
              {players.map((player) => {
                const displayId = player.playerId
                  ? (player.playerId.includes('-') ? player.playerId.split('-').slice(1).join('-') : player.playerId)
                  : 'N/A';
                return (
                  <div key={player._id} className="players-card-item pending-card-item" role="listitem">
                    <div className="players-card-top">
                      <div className="players-card-left">
                        <div className="players-card-name">{player.name || 'N/A'}</div>
                        <div className="players-card-sub">
                          <span className="players-card-playerid">{displayId}</span>
                          {player.role ? (
                            <>
                              <span className="players-card-dot" aria-hidden="true">•</span>
                              <span className="pending-card-role">{player.role}</span>
                            </>
                          ) : null}
                        </div>
                      </div>
                      <div className="players-card-right">
                        <div className="players-card-amount is-base">{formatCurrency(player.basePrice)}</div>
                      </div>
                    </div>

                    <div className="pending-card-actions">
                      <button
                        type="button"
                        className="pending-card-action pending-card-action--force"
                        onClick={() => onForceAuction(player._id)}
                        title="Put player back in auction with quota bypass"
                      >
                        Force auction
                      </button>
                      <button
                        type="button"
                        className="pending-card-action pending-card-action--assign"
                        onClick={() => onDirectAssign(player)}
                        title="Directly assign player to a team"
                      >
                        Direct assign
                      </button>
                    </div>
                  </div>
                );
              })}

              <div className="players-cards-footer">
                Showing {players.length} player{players.length !== 1 ? 's' : ''}
              </div>
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="results-table">
                  <thead>
                    <tr>
                      <th onClick={() => onSort('name')} className="sortable">
                        Player Name {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => onSort('role')} className="sortable">
                        Role {sortConfig.key === 'role' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="text-right">Base Price</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {players.map(player => (
                      <tr key={player._id}>
                        <td>
                          <div className="player-name-cell">
                            <span className="player-name">{player.name}</span>
                          </div>
                        </td>
                        <td>
                          {(player.playerId && (() => {
                            // Remove tournament code prefix (e.g., "PLTC002-001" -> "001")
                            const displayId = player.playerId.includes('-') 
                              ? player.playerId.split('-').slice(1).join('-')
                              : player.playerId;
                            return <span className="player-id">{displayId}</span>;
                          })()) || 'N/A'}
                        </td>
                        <td className="price-cell">{formatCurrency(player.basePrice)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => onForceAuction(player._id)}
                              style={{
                                padding: '6px 12px',
                                fontSize: '12px',
                                background: '#f59e0b',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: '500'
                              }}
                              title="Put player back in auction with quota bypass"
                            >
                              Force auction
                            </button>
                            <button
                              onClick={() => onDirectAssign(player)}
                              style={{
                                padding: '6px 12px',
                                fontSize: '12px',
                                background: '#4CAF50',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: '500'
                              }}
                              title="Directly assign player to a team"
                            >
                              Direct assign
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="table-footer">
                <div className="table-count">Showing {players.length} player{players.length !== 1 ? 's' : ''}</div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

// History Section Component
function HistorySection({ players, teams, formatCurrency, onEdit, onRevoke }) {
  const [isPhone, setIsPhone] = useState(() => window.innerWidth <= 600);
  const [historySearch, setHistorySearch] = useState('');

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 600px)');
    const onChange = (e) => setIsPhone(e.matches);
    // Initialize + subscribe (support older Safari)
    setIsPhone(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // Get all sold players sorted by soldAt time (chronological order)
  const historyPlayers = players
    .filter(p => {
      const status = String(p.auctionStatus || '').toLowerCase();
      return status === 'sold' && p.soldAt;
    })
    .sort((a, b) => {
      const timeA = new Date(a.soldAt).getTime();
      const timeB = new Date(b.soldAt).getTime();
      return timeB - timeA; // Most recent first
    });

  const filteredHistoryPlayers = useMemo(() => {
    const q = historySearch.trim().toLowerCase();
    if (!q) return historyPlayers;

    return historyPlayers.filter((p) => {
      const name = String(p.name || '').toLowerCase();
      const playerId = String(p.playerId || '').toLowerCase();
      const team = String(p.soldToName || '').toLowerCase();
      return name.includes(q) || playerId.includes(q) || team.includes(q);
    });
  }, [historyPlayers, historySearch]);

  return (
    <div className="section-card auction-history-card">
      <div className="section-header">
        <h2 className="section-title" style={{ marginBottom: 0 }}>Auction History</h2>
        <div className="section-controls">
          <input
            type="text"
            placeholder="Search history (player / ID / team)…"
            value={historySearch}
            onChange={(e) => setHistorySearch(e.target.value)}
            className="search-input"
          />
        </div>
      </div>
      <p style={{ marginBottom: '20px', color: '#64748b', fontSize: '14px' }}>
        Chronological list of all players sold during the auction (most recent first). Click Edit to modify player details.
      </p>

      {filteredHistoryPlayers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h3>{historySearch.trim() ? 'No results found' : 'No auction history'}</h3>
          <p>{historySearch.trim() ? 'Try a different search.' : 'No players have been sold yet.'}</p>
        </div>
      ) : (
        <>
          {isPhone ? (
            <div className="auction-history-cards" role="list">
              {filteredHistoryPlayers.map((player, index) => {
                const displayId = player.playerId
                  ? (player.playerId.includes('-') ? player.playerId.split('-').slice(1).join('-') : player.playerId)
                  : 'N/A';
                const soldAtText = player.soldAt ? new Date(player.soldAt).toLocaleString() : 'N/A';
                const rank = filteredHistoryPlayers.length - index;
                return (
                  <div key={player._id} className="auction-history-card-item" role="listitem">
                    <div className="auction-history-card-top">
                      <div className="auction-history-card-left">
                        <div className="auction-history-card-title-row">
                          <span className="auction-history-card-rank">#{rank}</span>
                          <span className="auction-history-card-name">{player.name || 'N/A'}</span>
                        </div>
                        <div className="auction-history-card-sub">
                          <span className="auction-history-card-playerid">{displayId}</span>
                          <span className="auction-history-card-dot" aria-hidden="true">•</span>
                          <span className="auction-history-card-time">{soldAtText}</span>
                        </div>
                      </div>
                      <div className="auction-history-card-right">
                        <div className="auction-history-card-price">{formatCurrency(player.soldPrice)}</div>
                      </div>
                    </div>

                    <div className="auction-history-card-mid">
                      <span className="team-badge auction-history-card-team">{player.soldToName || 'N/A'}</span>
                      {player.transactionType ? (
                        <span className={`auction-history-card-chip ${player.transactionType === 'DirectAssign' ? 'is-direct' : player.transactionType === 'ForceAuction' ? 'is-force' : ''}`}>
                          {player.transactionType === 'DirectAssign' ? 'Direct assign' : player.transactionType === 'ForceAuction' ? 'Force auction' : player.transactionType}
                        </span>
                      ) : null}
                    </div>

                    <div className="auction-history-card-actions">
                      <button type="button" className="auction-history-action auction-history-action--edit" onClick={() => onEdit(player)}>
                        Edit
                      </button>
                      <button type="button" className="auction-history-action auction-history-action--revoke" onClick={() => onRevoke(player._id)}>
                        Revoke
                      </button>
                    </div>
                  </div>
                );
              })}
              <div className="auction-history-cards-footer">
                Showing {filteredHistoryPlayers.length} player{filteredHistoryPlayers.length !== 1 ? 's' : ''} in auction history
              </div>
            </div>
          ) : (
            <>
              <div className="mobile-scroll-hint" aria-hidden="true">
                Swipe horizontally to see all columns →
              </div>
              <div className="auction-history-hscroll">
                <div className="table-container auction-history-table-container">
                  <table className="results-table">
                    <thead>
                      <tr>
                        <th className="text-right">#</th>
                        <th>Player Name</th>
                        <th>Player ID</th>
                        <th>Team</th>
                        <th className="text-right">Price</th>
                        <th className="text-right">Sold At</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHistoryPlayers.map((player, index) => (
                        <tr key={player._id}>
                          <td className="num-cell" style={{ fontWeight: '600', color: '#64748b' }}>{filteredHistoryPlayers.length - index}</td>
                          <td>
                            <div className="player-name-cell">
                              <span className="player-name">{player.name}</span>
                            </div>
                          </td>
                          <td>
                            {(player.playerId && (() => {
                              // Remove tournament code prefix (e.g., "PLTC002-001" -> "001")
                              const displayId = player.playerId.includes('-') 
                                ? player.playerId.split('-').slice(1).join('-')
                                : player.playerId;
                              return <span className="player-id">{displayId}</span>;
                            })()) || 'N/A'}
                          </td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <span className="team-badge">{player.soldToName || 'N/A'}</span>
                              {player.transactionType === 'DirectAssign' && (
                                <span style={{
                                  fontSize: '11px',
                                  color: '#8b5cf6',
                                  background: 'rgba(139, 92, 246, 0.1)',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  display: 'inline-block',
                                  width: 'fit-content'
                                }}>Direct assign</span>
                              )}
                              {player.transactionType === 'ForceAuction' && (
                                <span style={{
                                  fontSize: '11px',
                                  color: '#f59e0b',
                                  background: 'rgba(245, 158, 11, 0.1)',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  display: 'inline-block',
                                  width: 'fit-content'
                                }}>Force auction</span>
                              )}
                            </div>
                          </td>
                          <td className="price-cell">{formatCurrency(player.soldPrice)}</td>
                          <td className="text-right">
                            {player.soldAt ? new Date(player.soldAt).toLocaleString() : 'N/A'}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              <button
                                onClick={() => onEdit(player)}
                                style={{
                                  padding: '4px 10px',
                                  fontSize: '11px',
                                  background: '#3b82f6',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontWeight: '500'
                                }}
                                title="Edit player details"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => onRevoke(player._id)}
                                style={{
                                  padding: '4px 10px',
                                  fontSize: '11px',
                                  background: '#ef4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontWeight: '500'
                                }}
                                title="Revoke sale and refund team"
                              >
                                Revoke
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="table-footer">
                    <div className="table-count">Showing {filteredHistoryPlayers.length} player{filteredHistoryPlayers.length !== 1 ? 's' : ''} in auction history</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default AuctionResults;
